import { X } from '@phosphor-icons/react';
import { trackLine, type ClientShell } from '../../utils/callStats';
import type { DiagnosticsRow, DiagnosticsSaved } from '../../hooks/useCallDiagnostics';

const STATE_COLOR = (line: string) => (line === 'ok' ? 'text-[#34C759]' : 'text-[#FF9F0A]');

/** The overlay: one block per peer, track state before the numbers. */
export function CallDiagnostics({ rows, client, saved, notes = [], onClose }: {
  rows: DiagnosticsRow[]; client: { shell: ClientShell; ua: string }; saved: DiagnosticsSaved;
  /** Lines of the call's own, such as how often the far video had to be restarted. */
  notes?: string[];
  onClose: () => void;
}) {
  const savedLine = !saved ? 'Record: not yet' : 'at' in saved
    ? `Record: saved ${new Date(saved.at).toLocaleTimeString()}`
    : `Record: not saved (${saved.error})`;
  return (
    <div role="dialog" aria-label="Call diagnostics" data-call-diagnostics data-client={client.shell}
      className="absolute left-3 top-3 z-30 max-h-[calc(100%-24px)] w-[min(360px,calc(100%-24px))] overflow-y-auto rounded-[16px] bg-black/80 p-3 text-[13px] leading-[18px] text-white tabular-nums">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-semibold">Diagnostics</span>
        <button type="button" onClick={onClose} aria-label="Close diagnostics"
          className="ios-press flex h-8 w-8 items-center justify-center rounded-full bg-white/[0.14] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
          <X size={14} weight="bold" aria-hidden="true" />
        </button>
      </div>
      <p className="text-white/70">This side: {client.shell}</p>
      <p className="break-all text-white/50" data-ua>{client.ua}</p>
      <p className="text-white/70" data-record>{savedLine}</p>
      {notes.map((n) => <p key={n} className="text-white/70" data-diag-note>{n}</p>)}
      {rows.length === 0 && <p className="mt-2 text-white/70">No peer connection</p>}
      {rows.map((r) => {
        const v = trackLine(r.video);
        const a = trackLine(r.audio);
        return (
          <div key={r.peer} className="mt-2 border-t border-white/15 pt-2" data-peer={r.peer} data-ice={r.ice} data-video-state={v} data-audio-state={a}>
            <p className="font-semibold">{r.name}</p>
            <p>ICE {r.ice} · {r.connection}</p>
            <p>Video <span className={STATE_COLOR(v)}>{v}</span> · Audio <span className={STATE_COLOR(a)}>{a}</span></p>
            <p className="text-white/70">
              attached {r.video.attached ? 'yes' : 'no'}/{r.audio.attached ? 'yes' : 'no'} · live {r.video.live ? 'yes' : 'no'}/{r.audio.live ? 'yes' : 'no'} · muted {r.video.muted ? 'yes' : 'no'}/{r.audio.muted ? 'yes' : 'no'}
            </p>
            <p>In {r.sample.bitrateIn} kbps · Out {r.sample.bitrateOut} kbps</p>
            <p>Loss {r.sample.lossIn}% · RTT {r.sample.rttMs ?? '?'} ms</p>
            <p>FPS in {r.sample.fpsIn} · out {r.sample.fpsOut}{r.sample.frameW ? ` · ${r.sample.frameW}×${r.sample.frameH}` : ''}</p>
            <p data-diag-cost>Dropped {r.sample.dropIn ?? 'n/a'}/s · decode {r.sample.decodeMs ?? 'n/a'} ms</p>
            <p data-diag-out>Sending {r.sample.outW ? `${r.sample.outW}×${r.sample.outH}` : 'n/a'} · limit {r.sample.outLimit ?? 'n/a'}</p>
          </div>
        );
      })}
    </div>
  );
}
